/**
 * OneVoiceRadarStrip — algorithmic fresh-tracks feed on Home.
 *
 * Redesigned (iter_338d) to mirror the "Listen to ONE VOICE RADIO" header
 * aesthetic: centered hero heading (On-Air pill → "Algorithmic Feed" eyebrow
 * → massive 3-line title → gradient divider with subtitle). Matches
 * `pages/Home.js` lines ~339-396 exactly so the two strips read as siblings.
 *
 * Broken images: tracks with a non-loading cover are dropped from the grid
 * on the fly (onError) so the browser never paints a placeholder "?".
 */
import { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { Link } from 'react-router-dom';
import { Radio, Info, ArrowRight, Sparkles } from 'lucide-react';
import { useAuth } from '../../context/AuthContext';
import { useAbExperiment } from '../../hooks/useAbExperiment';
import { AIBadge } from '../common/AIBadge';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

const resolveCover = (t) => t?.cover_art || t?.artwork_r2_url || t?.artwork || null;

/** Hero header — used by both the populated and the skeleton states so the
 * two look like the same component in two states, not two components. */
const RadarHero = () => (
  <div className="mb-8 text-center md:text-left" data-testid="radar-headline">
    <div className="inline-flex items-center gap-2 px-3 py-1 border border-[#c084fc]/40 bg-[#c084fc]/10 mb-3">
      <Radio size={12} className="text-[#c084fc] animate-pulse" />
      <span className="text-[#c084fc] text-[10px] font-mono uppercase tracking-[0.3em]">Algorithmic Feed</span>
    </div>
    <h2 className="font-['Chakra_Petch'] uppercase leading-[0.95] tracking-tight">
      <span className="block text-xl md:text-2xl text-[#888] font-mono tracking-[0.5em]">Tuned to</span>
      <span className="block text-4xl sm:text-5xl md:text-6xl lg:text-7xl font-black mt-1 text-white">
        One Voice <span className="text-[#c084fc]">Radar</span>
      </span>
    </h2>
    <div className="mt-4 flex items-center justify-center md:justify-start gap-3">
      <div className="h-px flex-1 max-w-[180px] bg-gradient-to-r from-transparent via-[#c084fc]/60 to-transparent" />
      <p className="text-[#888] font-mono text-[11px] uppercase tracking-widest">Fresh tracks · refreshes every 24h</p>
      <div className="h-px flex-1 max-w-[180px] bg-gradient-to-r from-transparent via-[#9b59b6]/60 to-transparent" />
    </div>
  </div>
);

const SKELETON_GRADIENTS = [
  'from-[#2a1543] via-[#1a0b2a] to-[#0a0a0a]',
  'from-[#1a0b2a] via-[#2a1543] to-[#0a0a0a]',
  'from-[#3a1860] via-[#1a0b2a] to-[#0a0a0a]',
  'from-[#0a0a0a] via-[#2a1543] to-[#1a0b2a]',
  'from-[#2a1543] via-[#0a0a0a] to-[#3a1860]',
  'from-[#1a0b2a] via-[#3a1860] to-[#0a0a0a]',
];

export const OneVoiceRadarStrip = () => {
  const { token } = useAuth();
  const [tracks, setTracks] = useState([]);
  const [serverVariant, setServerVariant] = useState(null);
  const [loading, setLoading] = useState(true);
  const [expandedId, setExpandedId] = useState(null);
  const [brokenIds, setBrokenIds] = useState(() => new Set());
  // Recent viral-signal track_ids — Ultimate-tier auto re-share.
  // Tracks that hit ≥2× their hourly baseline get a "🔥 Trending" pill.
  const [viralIds, setViralIds] = useState(() => new Set());

  // A/B: onevoice_radar_save_rate — backend tells us the variant it
  // actually used when ranking, so we stamp save/view events with the
  // exact variant served (avoids client/server drift).
  const { variant: hookVariant, track: trackAb } = useAbExperiment('onevoice_radar_save_rate', {
    preAssignedVariant: serverVariant,
  });

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await axios.get(`${API}/discovery/radar`, {
          params: { limit: 12 },
          headers: token ? { Authorization: `Bearer ${token}` } : {},
        });
        if (!cancelled) {
          setTracks(res.data?.tracks || []);
          setServerVariant(res.data?.experiment?.variant || null);
        }
      } catch {
        /* fail-silent */
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    // Fetch viral signals in parallel — public endpoint, no auth required.
    (async () => {
      try {
        const r = await axios.get(`${API}/promote/viral-signals`, { params: { limit: 50 } });
        if (!cancelled) setViralIds(new Set((r.data?.signals || []).map((s) => s.track_id)));
      } catch {
        /* fail-silent */
      }
    })();
    return () => { cancelled = true; };
  }, [token]);

  const cards = useMemo(
    () => tracks
      .map((t) => ({ ...t, _cover: resolveCover(t) }))
      .filter((t) => t._cover && !brokenIds.has(t.id)),
    [tracks, brokenIds],
  );

  // ── COLD-START SKELETON ──────────────────────────────────────────────
  // Same hero, 6 stubbed tiles, "Waking up the algorithm…" line.
  if (loading || cards.length === 0) {
    return (
      <section
        className="preserve-dark relative mb-12 py-10 border border-[#1a1a1a] rounded-lg overflow-hidden bg-[#050505] w-full"
        data-testid="radar-strip-skeleton"
      >
        <div aria-hidden className="absolute inset-0 pointer-events-none opacity-70">
          <div className="absolute -top-24 left-1/4 w-[520px] h-[520px] rounded-full bg-[#9b59b6]/10 blur-3xl" />
          <div className="absolute -bottom-32 right-1/4 w-[520px] h-[520px] rounded-full bg-[#c084fc]/10 blur-3xl" />
        </div>
        <div className="w-full px-4 sm:px-6 md:px-8 relative">
          <RadarHero />
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-3 sm:gap-4" data-testid="radar-skeleton-grid">
            {SKELETON_GRADIENTS.map((g, i) => (
              <div
                key={i}
                className={`aspect-square rounded-md bg-gradient-to-br ${g} border border-[#1a1a1a] animate-pulse relative overflow-hidden`}
              >
                <div className="absolute inset-x-0 bottom-0 h-1/2 bg-gradient-to-t from-black via-black/60 to-transparent" />
                <div className="absolute bottom-3 left-3 right-3 space-y-1.5">
                  <div className="h-2.5 w-3/4 rounded-sm bg-[#2a1543]" />
                  <div className="h-2 w-1/2 rounded-sm bg-[#1a0b2a]" />
                </div>
                <div className="absolute top-2 right-2 w-5 h-5 rounded-full border border-[#c084fc]/30 bg-black/40 animate-[spin_3s_linear_infinite]" />
              </div>
            ))}
          </div>
          <p className="mt-6 text-center font-mono text-[11px] uppercase tracking-widest text-[#666]">
            <span className="inline-block w-2 h-2 rounded-full bg-[#c084fc] mr-2 animate-pulse" />
            Waking up the algorithm<span className="animate-pulse">…</span>
          </p>
        </div>
      </section>
    );
  }

  return (
    <section
      className="preserve-dark relative mb-12 py-10 border border-[#1a1a1a] rounded-lg overflow-hidden bg-[#050505] w-full"
      data-testid="radar-strip"
      data-variant={hookVariant}
    >
      {/* Ambient halo backdrop — matches OneVoice Radio aesthetic */}
      <div aria-hidden className="absolute inset-0 pointer-events-none opacity-70">
        <div className="absolute -top-24 left-1/4 w-[520px] h-[520px] rounded-full bg-[#9b59b6]/10 blur-3xl" />
        <div className="absolute -bottom-32 right-1/4 w-[520px] h-[520px] rounded-full bg-[#c084fc]/10 blur-3xl" />
      </div>

      <div className="w-full px-4 sm:px-6 md:px-8 relative">
        <RadarHero />

        {/* ═══ Card grid ═══ */}
        <div
          className="relative grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-3 sm:gap-4"
          data-testid="radar-grid"
        >
          {cards.map((t) => {
            const why = t.signals?.why || [];
            const expanded = expandedId === t.id;
            return (
              <div
                key={t.id}
                className="group relative rounded-md overflow-hidden bg-[#0a0a0a] border border-[#1a1a1a] hover:border-[#c084fc]/70 hover:shadow-[0_0_32px_rgba(192,132,252,0.25)] transition-all duration-300"
                data-testid={`radar-card-${t.id}`}
              >
                <Link
                  to={`/track/${t.id}`}
                  className="block"
                  onClick={() => trackAb('play', { track_id: t.id })}
                >
                  <div className="aspect-square relative overflow-hidden bg-black">
                    <img
                      src={t._cover}
                      alt={t.title}
                      className="absolute inset-0 w-full h-full object-cover group-hover:scale-[1.06] transition-transform duration-500"
                      loading="lazy"
                      onError={() => setBrokenIds((prev) => {
                        if (prev.has(t.id)) return prev;
                        const next = new Set(prev);
                        next.add(t.id);
                        return next;
                      })}
                    />
                    <div className="absolute inset-x-0 bottom-0 h-2/3 bg-gradient-to-t from-black via-black/70 to-transparent" />
                    <div className="absolute inset-x-0 top-0 h-[1.5px] bg-[#c084fc] opacity-0 group-hover:opacity-100 group-hover:translate-y-[calc(100%-2px)] transition-all duration-700 ease-out" />

                    {/* 🔥 Viral pill — Ultimate-tier auto re-share signal */}
                    {viralIds.has(t.id) && (
                      <div
                        className="absolute top-2 left-2 px-1.5 py-0.5 bg-gradient-to-r from-[#ff5500] to-[#ff8800] text-white text-[9px] font-bold uppercase tracking-widest rounded shadow-[0_0_12px_rgba(255,85,0,0.6)] flex items-center gap-1 animate-pulse"
                        data-testid={`radar-viral-pill-${t.id}`}
                      >
                        🔥 Trending
                      </div>
                    )}

                    {/* 👑 VIP Pick pill — yesterday's top vote by VIP members */}
                    {t.vip_pick && (
                      <div
                        className="absolute top-2 right-2 px-1.5 py-0.5 bg-gradient-to-r from-[#FFDA33] to-[#FF6BD6] text-black text-[9px] font-bold uppercase tracking-widest rounded shadow-[0_0_12px_rgba(255,107,214,0.6)] flex items-center gap-1"
                        data-testid={`radar-vip-pick-${t.id}`}
                        title={`VIP Pick — ${t.signals?.vip_pick_votes || ''} votes`}
                      >
                        👑 VIP Pick
                      </div>
                    )}

                    <div className="absolute inset-x-0 bottom-0 p-3">
                      <p className="text-white font-['Chakra_Petch'] text-sm leading-tight truncate group-hover:text-[#c084fc] transition-colors">
                        {t.title}
                      </p>
                      <p className="text-[#ccc] font-mono text-[10px] truncate">
                        {t.artist || t.username}
                      </p>
                      <div className="flex items-center gap-1 mt-1.5 flex-wrap">
                        {t.is_ai_content && <AIBadge size="xs" variant="subtle" />}
                        {t.signals?.cosign_count > 0 && (
                          <span className="text-[9px] font-mono uppercase text-[#FFDA33] bg-[#FFDA33]/15 border border-[#FFDA33]/40 px-1.5 py-[1px] rounded flex items-center gap-1">
                            <Sparkles size={8} /> {t.signals.cosign_count}× co-sign
                          </span>
                        )}
                      </div>
                    </div>

                    <button
                      type="button"
                      onClick={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        setExpandedId(expanded ? null : t.id);
                      }}
                      className="absolute top-2 right-2 px-2 py-[3px] bg-black/80 border border-[#c084fc]/50 text-[#c084fc] text-[9px] font-mono uppercase tracking-wider rounded-sm opacity-0 group-hover:opacity-100 transition-opacity flex items-center gap-1 hover:bg-[#c084fc]/25 backdrop-blur-sm"
                      data-testid={`radar-why-btn-${t.id}`}
                      aria-label="Why am I seeing this?"
                    >
                      <Info size={10} /> Why
                    </button>
                  </div>
                </Link>

                {expanded && why.length > 0 && (
                  <div
                    className="absolute inset-x-0 bottom-0 bg-black/95 border-t border-[#c084fc] p-3 backdrop-blur-md"
                    data-testid={`radar-why-panel-${t.id}`}
                  >
                    <p className="text-[#c084fc] font-mono text-[9px] uppercase tracking-wider mb-1.5">
                      Ranking signals
                    </p>
                    <ul className="space-y-1">
                      {why.slice(0, 3).map((w, i) => (
                        <li key={i} className="text-white font-mono text-[10px] flex items-start gap-1.5">
                          <ArrowRight size={9} className="text-[#c084fc] flex-shrink-0 mt-[2px]" />
                          <span className="truncate">{w}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
};

export default OneVoiceRadarStrip;
